<template>
  <div class="border border-neutral-200 rounded-md hover:shadow-lg h-full">
    <div class="relative h-56 lg:h-72 overflow-hidden">
      <!-- Free Shipping Badge -->
      <FreeShippingBadge
        v-if="product?.free_shipping?.is_available"
        :is-available="product.free_shipping.is_available"
        size="small"
        position="bottom-left"
      />

      <!-- Product Badges -->
      <ProductBadges :product="product" size="medium" position="top-left" />

      <!-- Promotional Banners (PrestaShop discount rules) -->
      <div class="absolute top-2 right-2 flex flex-col gap-2 items-end z-20" v-if="product.cart_rules && product.cart_rules.length > 0">
        <div v-for="rule in product.cart_rules">
          <VCartRule :rule="rule" />
        </div>
      </div>

      <NuxtLink :to="product.link" class="block">
        <NuxtImg
          v-if="image"
          :src="image"
          format="webp"
          :alt="product.name"
          loading="lazy"
          class="block object-contain w-auto h-56 lg:h-72 mx-auto rounded-md aspect-square"
          width="300"
          height="300"
        />
      </NuxtLink>
    </div>
    <div class="p-4 border-t border-neutral-200 flex flex-col h-[calc(100%-224px)] lg:h-[calc(100%-288px)]">
      <div class="flex flex-col grow h-full">
        <SfLink :href="product.link" variant="secondary" class="text-base font-medium no-underline line-clamp-2">
          {{ product?.name }}
        </SfLink>

        <!-- Product Rating -->
        <div v-if="product.reviews && numericRating > 0" class="flex items-center gap-2 mt-2">
          <VStarRating :value="numericRating" :max="5" size="md" />
          <span class="text-sm text-gray-600">{{ numericRating.toFixed(1) }}</span>
          <span class="text-xs text-gray-500">({{ product.reviews.count }})</span>
        </div>

        <VProductPrice
          :prices-data="{
            specific_prices: product.specific_prices,
            price: product.price,
            regular_price: product.regular_price,
            discount_percentage: product.discount_percentage,
          }"
          container-styles="pt-3 pb-4"
        />
        <span class="flex grow text-sm mb-1">
          {{ product.delivery_information }}
        </span>
      </div>
      <div class="flex gap-2 shrink-0">
        <VButton
          :disabled="loading || !isAvailable"
          variant="primary"
          class="w-full"
          v-if="product?.id_product_attribute == '0'"
          @click="
            addToCart({
              ...product,
              id_product: product?.id_product,
              id_product_attribute: product?.id_product_attribute,
              qty: 1,
            })
          "
        >
          <SfProgressCircular v-show="loading" />
          <SfIconShoppingCart size="sm" class="mr-2" />
          {{ addToCartText }}
        </VButton>
        <VButton v-else :href="product.link" variant="primary" class="w-full">
          {{ addToCartText }}
        </VButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { SfIconShoppingCart, SfLink, SfProgressCircular } from '@storefront-ui/vue';
import type { GetProductResponse } from '@vue-storefront/prestashop-api';
import VProductPrice from './VProductPrice.vue';
import { useAddToCartText } from '~/composables/useAddToCart';

// Define the free shipping type
type FreeShipping = {
  is_available: boolean;
  carriers: Array<{
    id_carrier: number;
    name: string;
    delay: string;
  }>;
  cost_list: string[];
};

interface ProductBadge {
  label: string;
  color: string;
  text_color: string;
}

// Extend the product type to include free_shipping and reviews
type ExtendedProduct = GetProductResponse['psdata'] & {
  free_shipping?: FreeShipping;
  badges?: ProductBadge[];
  reviews?: {
    average_rating: number;
    count: number;
  };
};

const props = defineProps<{ product: ExtendedProduct }>();

const product = computed(() => props.product);
const { execute: addToCart, loading } = useAddToCart();
const { text: addToCartText } = useAddToCartText(() => props.product);
const { isAvailable } = useCartItem(product);

const image = computed(() => {
  return (
    (product.value?.default_image as any)?.large?.url?.replace('home_default', 'large_default') ??
    product.value?.default_image?.url?.replace('home_default', 'large_default')
  );
});

// Computed property for numeric rating to avoid duplication
const numericRating = computed(() => {
  return Number(product.value?.reviews?.average_rating) || 0;
});
</script>
